import { Button } from '../../components/ui/button';
import {
  Database, RefreshCw, Clock, CheckCircle2, XCircle, Shield, AlertTriangle,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';

export const RegulatoryTab = ({
  regSources, regQueue, regQueueStats, regSyncing, onSync, onReview, onRefresh
}) => {
  return (
    <div className="space-y-6" data-testid="admin-regulatory-tab">
      {/* Queue Stats */}
      {regQueueStats && (
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { label: 'Pending Review', value: regQueueStats.pending, color: 'from-amber-500 to-orange-500', icon: Clock },
            { label: 'Approved', value: regQueueStats.approved, color: 'from-emerald-500 to-green-500', icon: CheckCircle2 },
            { label: 'Rejected', value: regQueueStats.rejected, color: 'from-red-500 to-rose-500', icon: XCircle },
            { label: 'High Confidence', value: regQueueStats.by_confidence?.high || 0, color: 'from-blue-500 to-indigo-500', icon: Shield },
            { label: 'Low Confidence', value: regQueueStats.by_confidence?.low || 0, color: 'from-slate-400 to-slate-500', icon: AlertTriangle },
          ].map((kpi, i) => (
            <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-500">{kpi.label}</span>
                <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${kpi.color} flex items-center justify-center`}>
                  <kpi.icon className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold font-outfit text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Data Sources */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4 flex items-center gap-2">
          <Database className="w-5 h-5" /> Data Sources
        </h3>
        <div className="space-y-3">
          {regSources.map(src => (
            <div key={src.source_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`reg-source-${src.source_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${src.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className="font-medium text-sm text-slate-900">{src.name}</span>
                  <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">{src.region}</span>
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                  <span>{src.record_count} cached records</span>
                  <span>{src.pending_updates} pending updates</span>
                  {src.last_sync && <span>Last sync: {new Date(src.last_sync).toLocaleString()}</span>}
                </div>
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => onSync(src.source_id)}
                disabled={regSyncing === src.source_id}
                data-testid={`sync-${src.source_id}`}
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${regSyncing === src.source_id ? 'animate-spin' : ''}`} />
                {regSyncing === src.source_id ? 'Syncing...' : 'Sync'}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Approval Queue */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit text-lg font-semibold text-[#0B3D91] flex items-center gap-2">
            <Clock className="w-5 h-5" /> Approval Queue
          </h3>
          <Button size="sm" variant="outline" onClick={onRefresh}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
        {regQueue.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No updates pending review. Sync a data source to detect changes.</p>
        ) : (
          <div className="space-y-2">
            {regQueue.map(update => (
              <div key={update.update_id} className="p-4 bg-slate-50 rounded-xl" data-testid={`reg-update-${update.update_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        update.confidence === 'high' ? 'bg-emerald-50 text-emerald-700' :
                        update.confidence === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                      }`}>{update.confidence}</span>
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{update.change_type?.replace('_', ' ')}</span>
                      <span className="text-xs text-slate-400">{update.region}</span>
                      {update.hs_code && <span className="text-xs font-mono bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{update.hs_code}</span>}
                    </div>
                    <p className="font-medium text-sm text-slate-900">{update.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{update.description}</p>
                    {update.old_value && update.new_value && (
                      <div className="flex items-center gap-2 mt-1.5 text-xs">
                        <span className="text-red-600 flex items-center gap-0.5"><ArrowDownRight className="w-3 h-3" />{update.old_value}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-emerald-600 flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" />{update.new_value}</span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-1">{update.affected_users_count} users affected · {update.source} · {new Date(update.created_at).toLocaleString()}</p>
                  </div>
                  {update.status === 'pending_review' && (
                    <div className="flex gap-1 ml-3">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs" onClick={() => onReview(update.update_id, 'approve')}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 px-3 text-xs text-red-600 hover:bg-red-50" onClick={() => onReview(update.update_id, 'reject')}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                  {update.status !== 'pending_review' && (
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      update.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                    }`}>{update.status}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
